import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from 'services'
import { useSnackbar } from 'contexts'
import { assertOk, rolesMatrixQueryKeys } from './useRolesMatrix'

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * Member shape returned by GET /roles/company/:roleId/members.
 * Mirrors the backend IRoleMember interface.
 */
export interface RoleMember {
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
}

// ── Query key ────────────────────────────────────────────────────────────────

export const roleMemberQueryKeys = {
  members: (roleId: string) => ['roles', 'company', roleId, 'members'] as const,
}

// ── Query hook ────────────────────────────────────────────────────────────────

/**
 * List all members of a role (built-in or custom).
 * Pass `role.builtinRoleId ?? role.id` as roleId.
 */
export const useRoleMembers = (roleId: string) => {
  return useQuery<RoleMember[]>({
    queryKey: roleMemberQueryKeys.members(roleId),
    queryFn: async () => {
      const response = await api.get(`/roles/company/${roleId}/members`)
      const body = response.data
      return Array.isArray(body) ? body : []
    },
    enabled: Boolean(roleId),
    staleTime: 1000 * 60 * 2,
  })
}

// ── Mutation: add members ─────────────────────────────────────────────────────

/**
 * Add one or more users to a role.
 * POST /roles/company/:roleId/members  body: { userIds }
 */
export const useAddRoleMembers = (roleId: string) => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, { userIds: string[] }>({
    mutationFn: ({ userIds }) =>
      api
        .post(`/roles/company/${roleId}/members`, { userIds })
        .then(assertOk)
        .then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleMemberQueryKeys.members(roleId) })
      queryClient.invalidateQueries({ queryKey: ['roles', 'company'] })
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
    },
    onError: (err) => {
      notify({ type: 'error', title: err.message || 'Failed to assign users' })
    },
  })
}

// ── Mutation: remove member ───────────────────────────────────────────────────

/**
 * Remove a single user from a role.
 * DELETE /roles/company/:roleId/members/:userId
 */
export const useRemoveRoleMember = (roleId: string) => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, string>({
    mutationFn: (userId) =>
      api
        .delete(`/roles/company/${roleId}/members/${userId}`)
        .then(assertOk)
        .then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleMemberQueryKeys.members(roleId) })
      queryClient.invalidateQueries({ queryKey: ['roles', 'company'] })
      queryClient.invalidateQueries({ queryKey: rolesMatrixQueryKeys.matrix() })
    },
    onError: (err) => {
      notify({ type: 'error', title: err.message || 'Failed to remove user' })
    },
  })
}
